/**
 * Track Zone Types
 *
 * Core domain types for track zone analysis. Defines the structure of zones,
 * metrics, and analysis results without any calculation logic.
 */

// ============================================================================
// Core Zone Types
// ============================================================================

/** Type of riding behavior in a zone */
export type TrackZoneType =
  | 'braking'
  | 'cornering'
  | 'acceleration'
  | 'straight'

/** Severity level for zone inconsistency */
export type ZoneSeverity = 'low' | 'medium' | 'high'

/**
 * Specific type of inconsistency detected.
 * These are actionable issues riders can work on.
 */
export type InconsistencyType =
  | 'braking-point' // WHERE braking starts
  | 'braking-intensity' // HOW HARD braking is
  | 'apex-speed' // MIN speed in corner
  | 'apex-location' // WHERE min speed occurs
  | 'throttle-point' // WHERE acceleration starts
  | 'throttle-intensity' // HOW HARD on gas
  | 'coasting-duration' // TIME in neutral throttle
  | 'entry-speed' // Speed entering the zone
  | 'exit-speed' // Speed exiting the zone
  | null

// ============================================================================
// Zone Structure
// ============================================================================

/** A detected zone on the track with consensus behavior and variance metrics */
export interface TrackZone {
  id: string
  type: TrackZoneType
  startDistance: number
  endDistance: number
  name?: string
  consensus: ZoneConsensusMetrics
  variance: ZoneVarianceMetrics
  impact: ZoneImpact
  centerPoint?: { lat: number; lng: number }
}

// ============================================================================
// Consensus Metrics - Average behavior across laps
// ============================================================================

export interface ZoneConsensusMetrics {
  avgEntrySpeed: number
  avgMinSpeed: number
  avgExitSpeed: number
  avgLeanAngle: number
  peakLeanAngle: number
  avgDeceleration: number
  peakDeceleration: number
  avgAcceleration: number
  avgGForce: number
}

// ============================================================================
// Variance Metrics - How much laps differ
// ============================================================================

export interface ZoneVarianceMetrics {
  speedCV: number
  leanAngleStdDev: number
  entryDistanceStdDev: number
  exitDistanceStdDev: number
  consistencyScore: number
  lapCount: number
  /** @deprecated Use primaryIssue instead */
  primaryInconsistency: 'speed' | 'line' | 'timing' | 'technique' | null
  primaryIssue: InconsistencyType
  details: ZoneVarianceDetails
}

/** Detailed variance breakdown for specific behaviors */
export interface ZoneVarianceDetails {
  brakingPointVariance: DistanceVariance
  brakeReleaseVariance: DistanceVariance
  brakingIntensityVariance: GForceVariance
  apexLocationVariance: ApexLocationVariance
  apexSpeedVariance: SpeedVariance
  throttlePointVariance: ThrottlePointVariance
  throttleIntensityVariance: GForceVariance
  coastingVariance: CoastingVariance
  entrySpeedVariance: SpeedVariance
  exitSpeedVariance: SpeedVariance
}

// Variance sub-types
export interface DistanceVariance {
  stdDev: number
  range: number
  isSignificant: boolean
}

export interface SpeedVariance {
  stdDev: number
  range: number
  isSignificant: boolean
}

export interface GForceVariance {
  stdDev: number
  range: number
  avgPeakG: number
  isSignificant: boolean
}

export interface ApexLocationVariance extends DistanceVariance {
  avgApexPercent: number
}

export interface ThrottlePointVariance extends DistanceVariance {
  avgThrottlePercent: number
}

export interface CoastingVariance {
  distanceStdDev: number
  distanceRange: number
  avgDistance: number
  isSignificant: boolean
}

// ============================================================================
// Impact Assessment
// ============================================================================

export interface ZoneImpact {
  estimatedTimeLossMs: number
  severity: ZoneSeverity
  rank: number
  insight: string
}

// ============================================================================
// Per-Lap Data
// ============================================================================

export interface ZoneLapData {
  lapId: string
  lapNumber: number
  sessionId?: string
  entrySpeed: number
  minSpeed: number
  exitSpeed: number
  entryDistance: number
  exitDistance: number
  peakLeanAngle: number
  zoneTimeMs: number
  isOutlier: boolean
  deviationScore: number
  brakingPointDistance: number | null
  brakeReleaseDistance: number | null
  peakBrakingG: number
  apexDistance: number
  /** Latitude of the apex (slowest) point — use for marker placement instead of distance lookup */
  apexLat: number
  /** Longitude of the apex (slowest) point — use for marker placement instead of distance lookup */
  apexLng: number
  throttlePointDistance: number | null
  peakThrottleG: number
  coastingDistance: number
}

// ============================================================================
// Analysis Result
// ============================================================================

export interface ZoneAnalysisResult {
  zones: TrackZone[]
  focusZones: TrackZone[]
  zoneLapData: Map<string, ZoneLapData[]>
  trackStats: TrackStats
}

export interface TrackStats {
  totalZones: number
  highVarianceZones: number
  overallConsistency: number
  totalTimeLossMs: number
  trackLength: number
}

// ============================================================================
// Configuration
// ============================================================================

export interface ZoneDetectionConfig {
  minZoneLength: number
  brakingThreshold: number
  corneringThreshold: number
  accelerationThreshold: number
  minSamplesPerZone: number
  boundaryTolerance: number
  /**
   * Minimum speed drop percentage to validate a zone as a true corner.
   * If a "cornering" zone doesn't show at least this much speed reduction,
   * it will be reclassified as a "straight" (likely a bent straight with
   * lean angle from GPS noise or road camber).
   * Default: 10 (10% speed drop required)
   */
  minCornerSpeedDropPercent: number
}

export const DEFAULT_ZONE_DETECTION_CONFIG: ZoneDetectionConfig = {
  minZoneLength: 20,
  brakingThreshold: -2,
  corneringThreshold: 10,
  accelerationThreshold: 2,
  minSamplesPerZone: 4,
  boundaryTolerance: 10,
  minCornerSpeedDropPercent: 10,
}

export interface NoiseToleranceConfig {
  gpsAccuracy: number
  speedNoise: number
  accelerationNoise: number
  minDistanceVariance: number
  minSpeedVariance: number
  minGVariance: number
}

export const DEFAULT_NOISE_TOLERANCE: NoiseToleranceConfig = {
  gpsAccuracy: 3,
  speedNoise: 2,
  accelerationNoise: 0.05,
  minDistanceVariance: 8,
  minSpeedVariance: 8,
  minGVariance: 0.15,
}
